import type {ReactElement} from 'react'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks/dist/use-safe-effect'
import {DelayedSpinner} from '@freckle/educator-entities/ts/common/components/spinner-wrapper/delayed-spinner'
import {staticExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import {useAsync} from '@freckle/educator-entities/ts/common/hooks/use-async'
import {useCourses} from '@freckle/educator-entities/ts/common/hooks/use-courses'
import type {CourseAttrs} from '@freckle/educator-entities/ts/roster/models/course'
import {isCourseActive} from '@freckle/educator-entities/ts/roster/models/course'
import {WithResources} from '@freckle/classroom/ts/common/components/with-resources'
import {fetchDefaultCourseIdForTeacher} from '@freckle/classroom/ts/common/models/default-course'
import {
  useCourseIdQueryParam,
  useCourseRgpIdQueryParam,
} from '@freckle/classroom/ts/hooks/use-params'
import {useCurrentCourseIdStore} from '@freckle/classroom/ts/store/current-course-id'

const rgpIdToCourseId = (rgpId: string | null, courses: CourseAttrs[]): number | null => {
  if (rgpId === null) return null

  const course = courses.find(course => course.renaissanceRPIdentifier === rgpId)

  return course?.id ?? null
}

export function CourseIdLoader({children}: {children: ReactElement}) {
  const courseRgpIdQueryParam = useCourseRgpIdQueryParam()
  const courseIdQueryParam = useCourseIdQueryParam()
  const courses = useCourses()

  const defaultCourseId = useAsync(
    () => fetchDefaultCourseIdForTeacher(),
    'fetchDefaultCourseForTeacher',
    [],
    {staleTime: 1000 * 60 * 60},
  )
  const {currentCourseId} = useCurrentCourseIdStore()

  const courseIdIsValid = (courses: CourseAttrs[], courseId: number) =>
    courses.some(course => isCourseActive(course) && course.id === courseId)

  return (
    <WithResources
      resources={{courses, defaultCourseId}}
      render={({courses, defaultCourseId}) => {
        // If an RGP ID is provided AND that ID can be converted to a course ID, use it. Otherwise, use the course ID from the query param.
        const courseId = rgpIdToCourseId(courseRgpIdQueryParam, courses) ?? courseIdQueryParam

        return (
          <CourseIdLoaderInternal
            courseId={
              courseId !== null && courseId !== 0 && courseIdIsValid(courses, courseId)
                ? courseId
                : (currentCourseId ?? defaultCourseId)
            }
          >
            {children}
          </CourseIdLoaderInternal>
        )
      }}
    />
  )
}

function CourseIdLoaderInternal({courseId, children}: {courseId: number; children: ReactElement}) {
  const {currentCourseId: courseIdFromStore, changeCurrentCourseId} = useCurrentCourseIdStore()

  useSafeEffectExtraDeps<{
    changeCurrentCourseId: (courseId: number) => void
  }>(
    ({changeCurrentCourseId}) => {
      changeCurrentCourseId(courseId)
    },
    [courseId],
    {
      changeCurrentCourseId: staticExtra(changeCurrentCourseId),
    },
  )

  if (courseIdFromStore === null) return <DelayedSpinner />
  return children
}
